import { getAppConfig } from "./config";

// 'localhost:8080'
// alert(getAppConfig().apiUrl);
const delayedPromise = (delayInS: number) => new Promise(r => setTimeout(() => r({}), delayInS * 1000))
export function loadCredentials() {
    return { idToken: localStorage.getItem('idToken'), refreshToken: localStorage.getItem('refreshToken') }
}
export function storeCredentials(idToken: string, refreshToken: string) {
    localStorage.setItem('idToken', idToken);
    localStorage.setItem('refreshToken', refreshToken);
}
export function clearCredentials() {
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
}

class ApiClient {
    signOutCallback: () => void = () => { };
    public setSignOutCallback(callback: () => void) {
        this.signOutCallback = callback;
    }
    // Fired with the fresh idToken after every successful refresh, so
    // AuthProvider can re-decode claims (e.g. a role change) instead of
    // routing on a stale currentUser until the next full reload.
    tokenRefreshedCallback: (idToken: string) => void = () => { };
    public setTokenRefreshedCallback(callback: (idToken: string) => void) {
        this.tokenRefreshedCallback = callback;
    }

    /**
     * Exchange the stored refresh token for a fresh idToken immediately —
     * Firebase mints it with CURRENT custom claims, unlike the stored token,
     * which keeps its claims until natural expiry (~1h). Used after flows
     * that change claims server-side (accepting an org invitation) so the
     * dashboard picks up the new role without a manual reload.
     */
    /**
     * Refresh, and force-log-out if the refresh itself FAILS.
     *
     * The force-logout below (`responseRequiresRefresh` on the retry) only
     * fires when the refresh SUCCEEDS and the API still answers 401 — which is
     * the rarer half. Every revocation outcome lands here instead:
     * `TOKEN_EXPIRED`, `USER_DISABLED`, `USER_NOT_FOUND` and
     * `INVALID_REFRESH_TOKEN` all make Google's `securetoken` endpoint answer
     * HTTP 400, so `getRefreshTokenFromRemote` throws and, before this wrapper
     * existed, that throw escaped past the callback entirely. A revoked or
     * disabled operator saw a generic error toast per request and a
     * signed-in-looking SPA forever.
     *
     * Rethrows after signing out: callers still need the failure.
     */
    private async refreshOrSignOut(refreshToken: string) {
        try {
            return await this.getRefreshTokenFromRemote(refreshToken);
        } catch (error) {
            // DASHBOARD-43 / MYSPORTSET-75: sign out ONLY when the identity
            // provider's answer proves the stored session is dead. Before this
            // test, every failure of the refresh landed here, so a dropped
            // request — `TypeError: Load failed` from the raw `fetch` three
            // frames down, captured in production on a Mobile Safari operator
            // in Cannes on 2026-10-06 — ran `clearCredentials()` and dropped a
            // working operator to the login screen. Google's securetoken
            // endpoint has not refused anything in that case; nothing has been
            // revoked; the next request would have refreshed fine.
            //
            // `revoked` is the narrow read of this docblock's own list above:
            // TOKEN_EXPIRED, USER_DISABLED, USER_NOT_FOUND and
            // INVALID_REFRESH_TOKEN all answer in the 4xx band, so the
            // revocation half is unchanged. A rejection with no response and a
            // 5xx are the two cases carved out — see `TokenRefreshFailure`.
            if (!(error instanceof TokenRefreshFailure) || error.revoked) {
                this.signOutCallback();
            }
            throw error;
        }
    }

    public async forceTokenRefresh(): Promise<string> {
        const credentials = loadCredentials();
        const { jwtToken, refreshToken } = await this.refreshOrSignOut(credentials?.refreshToken);
        storeCredentials(jwtToken, refreshToken);
        this.tokenRefreshedCallback(jwtToken);
        return jwtToken;
    }
    public async fetchApi<TOutput, TInput>({ endpoint, method, body }: { endpoint: string; method: string; body?: TInput }) {
        await delayedPromise(0.01);
        const url = getAppConfig().apiUrl;
        console.log('invoking', `${url}/${endpoint}`);

        const credentials = loadCredentials();
        let idToken = credentials?.idToken;

        const request: ApiRequestIdentity = { method, endpoint };
        const response = await sendRequest(request, () =>
            invokeApi({ fullUrl: `${url}/${endpoint}`, method, idToken, body }));
        const jsonResponse = await getJsonFromResponse<TOutput>(response);

        if (isResponseValid(response))
            return jsonResponse;

        if (responseRequiresRefresh(response)) {
            const { jwtToken, refreshToken } = await this.refreshOrSignOut(credentials?.refreshToken);
            idToken = jwtToken;
            storeCredentials(idToken, refreshToken);
            this.tokenRefreshedCallback(idToken);

            const response = await sendRequest(request, () =>
                invokeApi({ fullUrl: `${url}/${endpoint}`, method, idToken, body }));
            if (responseRequiresRefresh(response))
                this.signOutCallback();

            const jsonResponse = await getJsonFromResponse<TOutput>(response);
            if (isResponseValid(response))
                return jsonResponse;
        }

        const errorMessage = (jsonResponse as any)?.message;
        const errorCode = (jsonResponse as any)?.code;
        if (errorMessage) throw new ApiError(errorMessage, response.status, errorCode, { method, endpoint });
        throw new ApiRequestFailure('unreadable', response.status, request);
    }

    public async fetchApiWithFormData<TOutput>({ endpoint, method, formData }: { endpoint: string; method: string; formData: FormData }) {
        await delayedPromise(0.01);
        const url = getAppConfig().apiUrl;

        const credentials = loadCredentials();
        let idToken = credentials?.idToken;

        const request: ApiRequestIdentity = { method, endpoint };
        const response = await sendRequest(request, () =>
            invokeApiWithFormData({ fullUrl: `${url}/${endpoint}`, method, idToken, formData }));
        const jsonResponse = await getJsonFromResponse<TOutput>(response);

        if (isResponseValid(response))
            return jsonResponse;

        if (responseRequiresRefresh(response)) {
            const { jwtToken, refreshToken } = await this.refreshOrSignOut(credentials?.refreshToken);
            idToken = jwtToken;
            storeCredentials(idToken, refreshToken);
            this.tokenRefreshedCallback(idToken);

            const response = await sendRequest(request, () =>
                invokeApiWithFormData({ fullUrl: `${url}/${endpoint}`, method, idToken, formData }));
            if (responseRequiresRefresh(response))
                this.signOutCallback();

            const jsonResponse = await getJsonFromResponse<TOutput>(response);
            if (isResponseValid(response))
                return jsonResponse;
        }

        const errorMessage = (jsonResponse as any)?.message;
        const errorCode = (jsonResponse as any)?.code;
        if (errorMessage) throw new ApiError(errorMessage, response.status, errorCode, { method, endpoint });
        throw new ApiRequestFailure('unreadable', response.status, request);
    }

    /**
     * A request whose answer is NOT a JSON document, or one that must outlive
     * the page — the camera aim view's frame relay (`GET …/live-view/frame`,
     * raw `image/jpeg` plus `ETag` / `X-Frame-Age-Ms` headers) and its
     * `keepalive` DELETE sent from `pagehide`.
     *
     * Resolves with the `Response` itself for any 2xx AND for a 304, which a
     * conditional request (`If-None-Match`) gets when nothing changed: the
     * caller reads `status`, `headers` and the body. Every other status throws
     * exactly as `fetchApi` does (`ApiError` carrying the API's message, else
     * `ApiRequestFailure`), and authentication behaves the same: refresh and
     * retry once on a 401 — never on a 403 (see `responseRequiresRefresh`).
     *
     * `cache: 'no-store'` so the browser's own HTTP cache can never answer a
     * conditional request on our behalf and hide the 304 (and its fresh
     * `X-Frame-Age-Ms`) from the caller. The first `fetch` is issued before
     * any `await`, so a call made from a `pagehide` handler really leaves the
     * page. No per-call `console.log` either: the frame relay is polled every
     * second.
     */
    public async fetchRaw({ endpoint, method, headers, keepalive, signal }: RawRequestInput): Promise<Response> {
        const url = getAppConfig().apiUrl;
        const credentials = loadCredentials();
        const request: ApiRequestIdentity = { method, endpoint };
        const send = (idToken: string) => sendRequest(request, () =>
            invokeApiRaw({ fullUrl: `${url}/${endpoint}`, method, idToken, headers, keepalive, signal }));

        let response = await send(credentials?.idToken);
        if (isRawResponseUsable(response))
            return response;

        if (responseRequiresRefresh(response)) {
            const { jwtToken, refreshToken } = await this.refreshOrSignOut(credentials?.refreshToken);
            storeCredentials(jwtToken, refreshToken);
            this.tokenRefreshedCallback(jwtToken);

            response = await send(jwtToken);
            if (responseRequiresRefresh(response))
                this.signOutCallback();
            if (isRawResponseUsable(response))
                return response;
        }

        // An error body may be JSON from Nest, or an HTML page from the edge.
        const errorBody = await readErrorBody(response);
        const errorMessage = (errorBody as any)?.message;
        const errorCode = (errorBody as any)?.code;
        if (errorMessage) throw new ApiError(errorMessage, response.status, errorCode, { method, endpoint });
        throw new ApiRequestFailure('unreadable', response.status, request);
    }

    async getRefreshTokenFromRemote(
        refreshToken: string,
    ) {
        const { apiKey } = getAppConfig().firebaseConfig;

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `grant_type=refresh_token&refresh_token=${refreshToken}`,
        };

        let response: Response;
        try {
            response = await fetch(
                `https://securetoken.googleapis.com/v1/token?key=${apiKey}`,
                requestOptions,
            );
        } catch (error) {
            // No response was received, so nothing has been refused. This is
            // the branch that used to force-log a working operator out.
            throw new TokenRefreshFailure('network', 0, error);
        }

        if (!response.ok) {
            throw new TokenRefreshFailure(
                response.status >= 400 && response.status < 500
                    ? 'refused'
                    : 'unavailable',
                response.status,
            );
        }

        const result = await response.json();

        return {
            jwtToken: result.id_token,
            refreshToken: result.refresh_token,
        };
    }
}


/**
 * Send a request, converting a TRANSPORT failure into a typed, fingerprintable
 * one — DASHBOARD-1V / DASHBOARD-40.
 *
 * `fetch` rejects with a `TypeError` whose message is written by the BROWSER
 * when the request never completes (connection dropped mid-flight, DNS, TLS,
 * a refused CORS preflight, the edge closing the socket). Before this wrapper
 * that rejection escaped `fetchApi` unwrapped, with three consequences:
 *
 * 1. **One defect became N Sentry issues, one per browser wording.** WebKit
 *    says `Load failed` (DASHBOARD-1V, 6 users) and Chromium says
 *    `Failed to fetch` (DASHBOARD-40, 3 users); Firefox would add a third.
 *    Nothing but the message differs, and the default fingerprint IS the
 *    message, so 9 operators hitting one condition were filed as two unrelated
 *    tickets.
 * 2. **Neither named the request.** `classifyApiError` returns `null` for a
 *    non-`ApiError`, so the event carried no `api.route`, no `api.status` and
 *    no fingerprint — measured on the live events, whose `api.*` tags are
 *    empty while a sibling `ApiError` on the same page carries
 *    `api.route: POST events`. Nobody could tell WHICH call failed.
 * 3. `queryErrorReporting`'s offline breadcrumb described it as the literal
 *    string `a query`.
 *
 * The identity is threaded in from `fetchApi`, which is the only layer that
 * still knows the endpoint and method in their untemplated form.
 *
 * SCOPE: this types the failure, it does not swallow it (ledger D2 — a
 * transport failure may well be our own API being unreachable, which is a
 * signal). It still throws, and `queryClient` still captures it. The one place
 * a transport failure is deliberately NOT an error is an offline browser, and
 * that decision already lives in `queryErrorReporting.ts`.
 *
 * `send` is invoked INSIDE the try, so a synchronous throw from building the
 * request (`JSON.stringify` on a circular body) also lands here. That is
 * coarse but not wrong — no response was received either way — and the
 * underlying error's name and message are carried to Sentry in
 * `ApiRequestFailure.underlying` rather than into the fingerprint, which is
 * what keeps the browser wordings from re-splitting the group.
 */
async function sendRequest(
    request: ApiRequestIdentity,
    send: () => Promise<Response>,
): Promise<Response> {
    try {
        return await send();
    } catch (error) {
        throw new ApiRequestFailure('network', 0, request, error);
    }
}

function invokeApi(input: { fullUrl: string, method: string, idToken: string, body?: any }) {
    return fetch(input.fullUrl, {
        method: input.method,
        headers: {
            'Content-Type': 'application/json',
            'authorization': input.idToken
        },
        body: JSON.stringify(input.body),
    });
}

function invokeApiWithFormData(input: { fullUrl: string, method: string, idToken: string, formData: FormData }) {
    return fetch(input.fullUrl, {
        method: input.method,
        headers: {
            'authorization': input.idToken
            // Note: Don't set Content-Type for FormData, let the browser set it with boundary
        },
        body: input.formData,
    });
}

/** See `ApiClient.fetchRaw`. */
export interface RawRequestInput {
    endpoint: string;
    method: string;
    /** Extra request headers (e.g. `If-None-Match`); `authorization` is always set by the client. */
    headers?: Record<string, string>;
    /** Let the request finish after the page is gone (`pagehide`, tab close). */
    keepalive?: boolean;
    signal?: AbortSignal;
}

function invokeApiRaw(input: {
    fullUrl: string,
    method: string,
    idToken: string,
    headers?: Record<string, string>,
    keepalive?: boolean,
    signal?: AbortSignal,
}) {
    return fetch(input.fullUrl, {
        method: input.method,
        headers: {
            ...input.headers,
            'authorization': input.idToken,
        },
        cache: 'no-store',
        keepalive: input.keepalive,
        signal: input.signal,
    });
}

function isResponseValid(response: Response) {
    return response.status >= 200 && response.status < 300;
}
/** `fetchRaw` hands a 304 to its caller: "nothing changed" is an answer there. */
function isRawResponseUsable(response: Response) {
    return isResponseValid(response) || response.status === 304;
}
async function readErrorBody(response: Response): Promise<unknown> {
    try {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
    } catch {
        return null;
    }
}
export function responseRequiresRefresh(response: Pick<Response, 'status'>) {
    // Only 401 (expired/invalid token) should trigger a token refresh. 403 is a
    // legitimate authorization/precondition response (e.g. MYS-62 approval gates,
    // cross-org access) — refreshing on 403 retries, fails again, and force-logs
    // the admin out. Mirror the mobile client, which refreshes on 401 only.
    return response.status === 401;
}
/**
 * Parse a response body, mapping an EMPTY body to `null` — never `undefined`.
 *
 * DASHBOARD-41. `undefined` is the one value React Query v4 refuses to accept
 * from a `queryFn`: `query.ts` calls
 * `onError(new Error(\`${this.queryHash} data is undefined\`))`, so a read that
 * resolves `undefined` is reported to the caller as a FAILED query and
 * captured by Sentry — titled with the query key, which is why the production
 * issue reads `["cgu","<orgId>","<typeId>"] data is undefined`.
 *
 * An empty body is not an error here. `isResponseValid` admits the whole 2xx
 * range, so `fetchApi` RETURNS this value rather than throwing on:
 *   - an explicit 204 (`GET /membership-subscriptions/:org/:type/cgu` answers
 *     204 whenever no CGU is configured — the normal case for a membership
 *     type without one, which is why `/admin/memberships` errored in prod);
 *   - any 2xx from a Nest handler that returned `null`/`undefined`, which the
 *     express adapter serialises as a 200 with no body at all.
 *
 * `null` is a legal React Query data value, so the same read now resolves as
 * "answered, no content" — which is what every caller's docblock already
 * claimed it did. See `features/_shared/empty-body-read.test.ts` for the pin on
 * React Query's side of this contract: it is a third-party invariant, so the
 * test asserts BOTH halves (undefined rejected, null accepted) rather than
 * trusting this comment to stay true across an upgrade.
 *
 * Deliberately NOT changed: the error path below reads
 * `(jsonResponse as any)?.message`, and `null?.message` is `undefined` exactly
 * as `undefined?.message` was — a non-2xx with an empty body still falls
 * through to the no-message throw, which is now an
 * `ApiRequestFailure('unreadable', status)` rather than a bare
 * `Error('An error occured')` (DASHBOARD-3K).
 */
async function getJsonFromResponse<T>(response: Response) {
    const text = await response.text();
    if (!text) return null as T;
    return JSON.parse(text) as T;
}

/** Which call failed. See `ApiError.request`. */
export interface ApiRequestIdentity {
    /** HTTP method, as passed to `fetchApi`. */
    method: string;
    /** Endpoint as called, query string included and NOT templated. */
    endpoint: string;
}

/** Why a request produced no usable API error message. */
export type ApiRequestFailureKind =
    /** `fetch` rejected: no response was received at all. */
    | 'network'
    /** A response arrived, non-2xx, with no `message` in its body. */
    | 'unreadable';

/**
 * A request that failed WITHOUT the API saying why — DASHBOARD-1V / -40 / -3K.
 *
 * `ApiError` means "the API refused and told us what was wrong", and the whole
 * reporting pipeline is built on that: `isExpectedApiRejection` filters a 4xx
 * to a breadcrumb precisely BECAUSE the operator was shown its message. The two
 * failures below carry no such message, so they are a different thing and get a
 * different type:
 *
 *   - `network`   — `fetch` rejected; there is no status and no body.
 *   - `unreadable` — a real status, but a body with no `message` (an empty
 *                    body, a plain-text or HTML error page from the edge, a
 *                    502/504 that never reached Nest).
 *
 * ⚠️ DELIBERATELY NOT A SUBCLASS OF `ApiError`, and that is the load-bearing
 * decision here. Sixteen call sites branch on `instanceof ApiError`, and two of
 * them (`AcceptInvitationPage`, `ChangeUserPasswordModal`) show `error.message`
 * to the operator *in place of* a translated fallback. Both of these failures
 * have nothing worth showing — a browser-authored English `Load failed`, or a
 * status with no explanation — so inheriting `ApiError` would have swapped a
 * correct translated message for a useless literal on surfaces this defect was
 * never about. The user-facing behaviour is therefore UNCHANGED by this fix; it
 * is a reporting fix (see `utils/apiErrorReporting.ts`), and the user-facing
 * half is already correct because these stay non-`ApiError`.
 *
 * Carries no `code`: `expectedErrors.ts`'s `shouldReportToSentry` filters on a
 * string `code`, and a type that must always be reported must not offer that
 * field for a future author to populate.
 */
export class ApiRequestFailure extends Error {
    constructor(
        public readonly kind: ApiRequestFailureKind,
        /** The HTTP status, or `0` when no response was received. */
        public readonly status: number,
        /** Which call failed. Never optional — the point of this type. */
        public readonly request: ApiRequestIdentity,
        /**
         * The rejection this wraps, for the Sentry `extra`.
         *
         * Named `underlying` rather than `cause` on purpose: `cause` is a
         * standard `Error` field, and shadowing it with a differently-typed
         * own property is how a redaction or serialisation helper quietly
         * starts reading the wrong thing.
         */
        public readonly underlying?: unknown,
    ) {
        super(
            kind === 'network'
                ? 'No response was received from the server'
                : `The server returned an error (${status})`,
        );
        // Sentry titles an event `${name}: ${message}`. Without this both kinds
        // would render as a bare `Error`, indistinguishable in a list from the
        // generic throws this type exists to replace.
        this.name = 'ApiRequestFailure';
    }
}

/** Why exchanging the stored refresh token for a fresh idToken failed. */
export type TokenRefreshFailureKind =
    /** `fetch` rejected: the request never reached the identity provider. */
    | 'network'
    /** A 4xx: the identity provider REFUSED the stored credential. */
    | 'refused'
    /** A 5xx (or any other non-2xx): a fault on the provider's side. */
    | 'unavailable';

/**
 * A failed token refresh — DASHBOARD-43, and its mobile twin MYSPORTSET-75.
 *
 * ⚠️ `revoked` IS THE WHOLE POINT OF THIS TYPE, and it is an auth POLICY, not a
 * reporting detail. `refreshOrSignOut` runs `clearCredentials()` on it, so
 * answering it wrongly either keeps a revoked admin signed in (too narrow) or
 * throws a working operator out mid-session (too wide — the live defect).
 *
 * Only a 4xx proves the stored refresh token is dead. Every revocation outcome
 * Google's `securetoken` endpoint reports is in that band — `TOKEN_EXPIRED`,
 * `USER_DISABLED`, `USER_NOT_FOUND`, `INVALID_REFRESH_TOKEN` are all HTTP 400 —
 * which is why the band, and not a retry count, is the discriminator. The two
 * carve-outs are the cases where the provider said nothing about the
 * credential at all:
 *
 *   - `network`     — `fetch` rejected. The browser writes this message
 *                     (`TypeError: Load failed` on WebKit, `Failed to fetch`
 *                     on Chromium), so there is no provider verdict in it.
 *   - `unavailable` — a 5xx. The provider is down; the credential is untested.
 *
 * Before this type the refresh threw a bare `Error('Network response was not
 * ok')` for the second case and let the raw `TypeError` escape for the first,
 * so BOTH were indistinguishable from a revocation at the one call site that
 * had to tell them apart — and indistinguishable from each other in Sentry,
 * where a bare `Error` is fingerprinted on its message (D99: the types a client
 * throws are part of its observability surface).
 *
 * Carries no `code`, for the same reason `ApiRequestFailure` does not:
 * `expectedErrors.ts`'s `shouldReportToSentry` filters on a string `code`, and
 * a failure that must always be reported must not offer that field.
 */
export class TokenRefreshFailure extends Error {
    /**
     * True only when the provider's answer proves the stored refresh token is
     * no longer usable — the ONLY state in which signing the operator out is
     * correct. Derived from `kind` in one place, and an own enumerable
     * property so it reaches Sentry's serialization.
     */
    public readonly revoked: boolean;

    constructor(
        public readonly kind: TokenRefreshFailureKind,
        /** The HTTP status, or `0` when no response was received. */
        public readonly status: number,
        /** The rejection this wraps, for the Sentry `extra`. See `ApiRequestFailure.underlying`. */
        public readonly underlying?: unknown,
    ) {
        super(
            kind === 'network'
                ? 'The session refresh never reached the identity provider'
                : kind === 'refused'
                    ? `The identity provider refused the stored session (${status})`
                    : `The identity provider is unavailable (${status})`,
        );
        // Sentry titles an event `${name}: ${message}`; without this all three
        // kinds render as a bare `Error`.
        this.name = 'TokenRefreshFailure';
        this.revoked = kind === 'refused';
    }
}

export class ApiError extends Error {
    /**
     * The request that was refused.
     *
     * DASHBOARD-2: every `ApiError` is thrown from the same two lines of this
     * file, so its stack is one frame and Sentry grouped 13 unrelated failures
     * into a single untriageable issue titled after whichever arrived last.
     * `utils/apiErrorReporting.ts` fingerprints on this, so each endpoint gets
     * its own issue.
     *
     * Optional because callers may construct an `ApiError` by hand; the
     * reporting path degrades to an `unknown` route rather than throwing.
     */
    constructor(
        public message: string,
        public status: number,
        public code?: string,
        public request?: ApiRequestIdentity,
    ) {
        super(message);
    }
}

const client = new ApiClient();
export const useApiClient = () => {
    return client;
};